import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { StatusTag, normalizeStatusTone } from "@/views/components/StatusTag";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { confirmDialog } from "@/utils/confirmDialog";
import { PUSH_RESULT } from "@zhihaoscm/meta";
import type { CallbackRecord, CallbackRecordPageResult, CallbackRecordQuery } from "./types";
import { dateTimeFormat } from "@zhihaoscm/utils";

export interface CallbackRecordPageProps {
    loadRecords?: (query: CallbackRecordQuery) => Promise<CallbackRecordPageResult>;
    retryRecord?: (id: number | string) => Promise<void>;
}

const defaultQuery: CallbackRecordQuery = { page: 1, size: 10 };
export type CallbackRecordFilterValues = {
    keyword?: string;
    state?: number | string;
    pushTime?: [Dayjs | string, Dayjs | string] | null;
};

/** 将 Vue 日期范围筛选转换为回调分页接口的当天起止时间参数。 */
export function buildCallbackRecordFilters(
    values: CallbackRecordFilterValues
): Omit<CallbackRecordQuery, "page" | "size"> {
    const { pushTime, ...rawFilters } = values;
    const filters = Object.fromEntries(
        Object.entries(rawFilters).filter(([, value]) => value !== undefined && value !== null && value !== "")
    ) as Omit<CallbackRecordQuery, "page" | "size">;
    if (pushTime?.[0]) {
        const start = typeof pushTime[0] === "string" ? pushTime[0] : pushTime[0].format("YYYY-MM-DD");
        filters.beginTime = `${start} 00:00:00`;
    }
    if (pushTime?.[1]) {
        const end = typeof pushTime[1] === "string" ? pushTime[1] : pushTime[1].format("YYYY-MM-DD");
        filters.endTime = `${end} 23:59:59`;
    }
    if (filters.state !== undefined) filters.state = Number(filters.state);
    return filters;
}

/** 对齐 Vue ProTable 的文本缺省值，避免空回调字段渲染为空白。 */
const formatCallbackValue = (value?: string | number | null) => value || DEFAULT_VALUE;

/** 沿用 Vue 应用名称列的“名称 | ID”结构并统一空值文案。 */
const formatCallbackApplication = (name?: string, applicationId?: number | string) =>
    `${formatCallbackValue(name)} | ${formatCallbackValue(applicationId)}`;

/** 使用 Vue PUSH_RESULT 元数据渲染状态，未知状态不误判为失败。 */
const formatCallbackResult = (result?: number) => {
    if (result === PUSH_RESULT.SUCCESS.code) return { color: "green", label: "成功" };
    if (result === PUSH_RESULT.FAIL.code) return { color: "red", label: "失败" };
    return { color: undefined, label: DEFAULT_VALUE };
};

/** 将 Vue 回调分页返回的 pushResult 枚举统一为状态文案和重试操作使用的数字。 */
function normalizeCallbackRecords(records?: CallbackRecord[]): CallbackRecord[] {
    return (records || []).map(record => ({
        ...record,
        pushResult: record.pushResult == null ? undefined : Number(record.pushResult)
    }));
}

/** 对齐 Vue 回调记录的 dateTimeFormat，隐藏秒级精度。 */
const formatCallbackTime = (value?: string) => (value ? dateTimeFormat(value) : DEFAULT_VALUE);

/** 复用旧 Vue callbackService `/application-callback-record/paging` 接口的回调记录请求。 */
export async function loadCallbackRecords(query: CallbackRecordQuery): Promise<CallbackRecordPageResult> {
    const response = await request.service.get<CallbackRecordPageResult>("/application-callback-record/paging", {
        params: query
    });
    return response.data;
}

/** 复用旧 Vue `/application-callback-record/re-callback/:id` 接口重新发送失败回调。 */
export async function retryCallbackRecord(id: number | string): Promise<void> {
    await request.service.put(`/application-callback-record/re-callback/${id}`);
}

/** 平台管理-回调记录页面，覆盖回调状态和失败信息的列表主流程。 */
export function CallbackRecordPage({
    loadRecords = loadCallbackRecords,
    retryRecord = retryCallbackRecord
}: CallbackRecordPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<CallbackRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<CallbackRecordQuery>(defaultQuery);
    const [filterForm] = Form.useForm<CallbackRecordFilterValues>();

    /** 重试完成后重新读取当前页，确保状态从失败同步为最新结果。 */
    const reloadRecords = useCallback(async () => {
        const result = await loadRecords(query);
        setRecords(normalizeCallbackRecords(result.list));
        setTotal(result.total || 0);
    }, [loadRecords, query]);

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setRecords(normalizeCallbackRecords(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("回调记录加载失败");
                    setRecords([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    // 将 Vue 回调记录筛选字段转换为后端约定的查询参数。
    const submitFilters = (values: CallbackRecordFilterValues) => {
        const filters = buildCallbackRecordFilters(values);
        setErrorMessage(null);
        setQuery({ page: 1, size: 10, ...filters });
    };

    // 清空筛选条件并恢复 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    /** 确认后重试接口并刷新列表，失败时保留当前记录并反馈错误。 */
    const retryAfterConfirm = useCallback(
        async (id: number | string) => {
            if (id == null) return;
            try {
                await retryRecord(id);
                await reloadRecords();
                setErrorMessage(null);
                message.success("重新发送回调请求成功");
            } catch {
                setErrorMessage("回调重试失败");
            }
        },
        [reloadRecords, retryRecord]
    );

    /** 对齐 Vue confirmable 装饰器：显式确认后才触发回调重试请求。 */
    const handleRetry = useCallback(
        (id: number | string | undefined) => {
            if (id == null) return;
            void confirmDialog("是否重新发送回调请求", "重新回调", {
                onOk: () => retryAfterConfirm(id)
            });
        },
        [retryAfterConfirm]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue ZhTable 的 formatter 与 operation slot。
    const callbackColumns = useMemo<TableColumnProps<CallbackRecord>[]>(
        () => [
            {
                key: "application",
                label: "应用名称",
                prop: "applicationId",
                width: 200,
                render: ({ row }) => (
                    <Button
                        aria-label="查看应用详情"
                        type="link"
                        onClick={() =>
                            void navigate(`/manageCenter/appManagement/applicationDetail/${row.applicationId || ""}`)
                        }
                    >
                        {formatCallbackApplication(row.appName, row.applicationId)}
                    </Button>
                )
            },
            {
                key: "callbackUrl",
                label: "回调地址",
                prop: "callbackUrl",
                render: ({ cellValue }) => formatCallbackValue(cellValue as string | undefined)
            },
            {
                key: "pushJson",
                label: "请求参数",
                prop: "pushJson",
                render: ({ cellValue }) => formatCallbackValue(cellValue as string | undefined)
            },
            {
                key: "errorReason",
                label: "失败原因",
                prop: "errorReason",
                render: ({ cellValue }) => formatCallbackValue(cellValue as string | undefined)
            },
            {
                key: "pushResult",
                label: "状态",
                prop: "pushResult",
                width: 68,
                render: ({ cellValue }) => {
                    const status = formatCallbackResult(cellValue as number | undefined);
                    return <StatusTag tone={normalizeStatusTone(status.color)}>{status.label}</StatusTag>;
                }
            },
            {
                key: "pushTime",
                label: "回调时间",
                prop: "pushTime",
                width: 154,
                render: ({ cellValue }) => formatCallbackTime(cellValue as string | undefined)
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    row.pushResult === PUSH_RESULT.FAIL.code ? (
                        <Button disabled={banButton} type="link" onClick={() => handleRetry(row.id)}>
                            重新回调
                        </Button>
                    ) : null
            }
        ],
        [banButton, handleRetry, navigate]
    );

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="callback-record-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<CallbackRecord>
                    className="flex-1"
                    columns={normalizeOperationColumns(callbackColumns, records)}
                    data={records}
                    pagination={false}
                    rowKey={record => record.id || `${record.applicationId}-${record.pushTime}` || "callback"}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                回调记录
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                <Form.Item noStyle name="keyword">
                                    <Input placeholder="请输入应用名称或者应用ID搜索" />
                                </Form.Item>
                                <Form.Item noStyle name="state">
                                    <Select
                                        allowClear
                                        aria-label="回调状态"
                                        options={PUSH_RESULT.options}
                                        placeholder="请选择状态"
                                        style={{ width: 90 }}
                                    />
                                </Form.Item>
                                <Form.Item noStyle name="pushTime">
                                    <ZhDateRangePicker
                                        endPlaceholder="回调结束日期"
                                        format="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="回调开始日期"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                    />
                                </Form.Item>
                                <Button htmlType="submit" type="primary">
                                    搜索
                                </Button>
                                <Button className="table-filter-reset-button" onClick={resetFilters}>
                                    重置
                                </Button>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 保留旧 Vue el-pagination 的可选分页大小，避免迁移后无法选择 10/30/40 条。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default CallbackRecordPage;
